import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Checkbox,
  FormControlLabel,
  TextField,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listUsers } from '../services/userManagementService';
import { sendNotification } from '../services/notificationsService';
import dailyCoService from '../services/dailyCoService';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 400 },
  maxWidth: 400,
  maxHeight: { xs: '90vh', sm: '85vh' },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const StartVideoCallModal = ({ open, handleClose, userType }) => {
  const { user, tenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [callableUsers, setCallableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addSponsor, setAddSponsor] = useState(false);
  const [addPhysician, setAddPhysician] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [otherPhysicians, setOtherPhysicians] = useState([]);
  const [loadingSponsors, setLoadingSponsors] = useState(false);
  const [loadingPhysicians, setLoadingPhysicians] = useState(false);
  const [callType, setCallType] = useState('video');
  const [callNotes, setCallNotes] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      if (!user || !tenant) {
        return;
      }

      setLoading(true);
      try {
        // Determine target role based on userType
        let targetRole = null;
        if (userType === 'patient') {
          targetRole = 'physician'; // Patients call physicians
        } else if (userType === 'physician') {
          targetRole = 'patient'; // Physicians call patients
        }
        // For super_admin or other roles, targetRole remains null (show all users)

        // Fetch all users with the target role (not dependent on appointments)
        const { data } = await listUsers({ 
          tenantId: tenant.id, 
          ...(targetRole && { role: targetRole }),
          page: 1,
          pageSize: 100 // Get more users
        });
        
        setCallableUsers(data?.rows || []);
      } catch (error) {
        console.error("Failed to fetch users:", error);
        setCallableUsers([]);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open, userType, user, tenant]);

  // Fetch sponsors when addSponsor checkbox is checked
  useEffect(() => {
    const fetchSponsors = async () => {
      if (!tenant || !addSponsor) {
        return;
      }

      setLoadingSponsors(true);
      try {
        const { data } = await listUsers({ tenantId: tenant.id, role: 'sponsor' });
        setSponsors(data?.rows || []);
      } catch (error) {
        console.error("Failed to fetch sponsors:", error);
        setSponsors([]);
      } finally {
        setLoadingSponsors(false);
      }
    };

    fetchSponsors();
  }, [addSponsor, tenant]);

  // Fetch other physicians when addPhysician checkbox is checked
  useEffect(() => {
    const fetchPhysicians = async () => {
      if (!tenant || !addPhysician || !user) {
        return;
      }

      setLoadingPhysicians(true);
      try {
        const { data } = await listUsers({ tenantId: tenant.id, role: 'physician' });
        // Filter out current user if they're a physician
        const filteredPhysicians = data?.rows?.filter(p => p.id !== user.id) || [];
        setOtherPhysicians(filteredPhysicians);
      } catch (error) {
        console.error("Failed to fetch physicians:", error);
        setOtherPhysicians([]);
      } finally {
        setLoadingPhysicians(false);
      }
    };

    fetchPhysicians();
  }, [addPhysician, tenant, user]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const handleStartCall = async () => {
    if (!selectedUser) {
      // Maybe show an error message
      return;
    }

    setLoading(true);

    try {
      // Build participants list
      const participants = [selectedUser.id];
      if (addSponsor && selectedSponsor) {
          participants.push(selectedSponsor.id);
      }
      if (addPhysician && selectedPhysician) {
          participants.push(selectedPhysician.id);
      }

      // Create a unique call session ID that will be used as the room identifier
      // This ensures both parties join the same room
      const callSessionId = `call-${crypto.randomUUID()}`;

      // Create the room via backend API FIRST - this ensures the room exists
      // and both parties will use the same room name
      const roomResponse = await dailyCoService.createVideoCallRoom(
        callSessionId,
        `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'Caller'
      );

      if (!roomResponse?.roomName) {
        throw new Error('Failed to create video call room');
      }

      // Use the room name from backend (e.g., "apt-call-xxx")
      const roomName = roomResponse.roomName;

      // Build query params for caller - use roomName as appointmentId to ensure same room
      const params = new URLSearchParams({
        appointmentId: roomName, // Use actual room name (with apt- prefix) to ensure same room
        participants: participants.join(','),
        type: callType,
      });
      
      if (callNotes.trim()) {
        params.append('notes', callNotes.trim());
      }

      // Get caller name
      const callerName = user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Someone';
      const callTypeText = callType === 'video' ? 'video call' : 'audio call';
      
      // Send notifications to all participants with the correct room info
      // CRITICAL: Use roomName (with apt- prefix) as appointmentId in notification URL
      // This ensures both parties use the exact same room identifier
      const notificationParams = new URLSearchParams({
        appointmentId: roomName, // Use actual room name (with apt- prefix) for consistency
        participants: participants.join(','),
        type: callType,
      });
      
      const notificationPromises = participants.map(async (participantId) => {
        try {
          await sendNotification({
            user_id: participantId,
            tenant_id: tenant?.id,
            title: `Incoming ${callTypeText}`,
            message: `${callerName} is calling you${callNotes.trim() ? `: ${callNotes.trim()}` : ''}`,
            type: 'info',
            metadata: {
              roomId: roomName, // Use the actual room name from backend (with apt- prefix)
              appointmentId: callSessionId, // Keep original for reference/logging
              roomName: roomName, // Explicitly pass the room name
              callType,
              callerId: user?.id,
              actionUrl: `/video-call?${notificationParams.toString()}`,
            },
          });
        } catch (error) {
          console.error(`Failed to send notification to user ${participantId}:`, error);
          // Don't block the call if notification fails
        }
      });

      // Send notifications in parallel (don't wait for them to complete)
      Promise.all(notificationPromises).catch(err => {
        console.error('Error sending some notifications:', err);
      });

      // Navigate to video call with the room identifier
      navigate(`/video-call?${params.toString()}`);
      handleClose();
    } catch (error) {
      console.error('Error starting video call:', error);
      alert('Failed to start video call. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderUserList = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (callableUsers.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          No users available to call
        </Typography>
      );
    }

    return (
      <List sx={{ py: 0 }}>
        {callableUsers.map((user) => (
          <ListItem
            key={user.id}
            disablePadding
          >
            <ListItemButton
              selected={selectedUser?.id === user.id}
              onClick={() => handleUserSelect(user)}
              sx={{ py: { xs: 1, sm: 1.5 } }}
            >
              <ListItemAvatar>
                <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                  {(user.name || user.first_name || user.firstName || 'U').charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={user.name || `${user.first_name || user.firstName || ''} ${user.last_name || user.lastName || ''}`.trim() || 'Unknown User'}
                secondary={user.specialty || user.tenant_role || ''}
                primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="start-video-call-modal-title"
      aria-describedby="start-video-call-modal-description"
    >
      <Box sx={style}>
        {/* Header - Fixed */}
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Typography id="start-video-call-modal-title" variant="h6" component="h2">
            Start a Video Call
          </Typography>
          <Typography id="start-video-call-modal-description" sx={{ mt: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Select who you want to call.
          </Typography>
        </Box>
        
        {/* Scrollable Content Area */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          minHeight: 0, // Important for flex scrolling
          pr: { xs: 0.5, sm: 0 }, // Add padding for scrollbar on mobile
        }}>
          {renderUserList()}
        {userType === 'patient' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={addSponsor}
                onChange={(e) => setAddSponsor(e.target.checked)}
                name="addSponsor"
                color="primary"
                size="small"
              />
            }
            label="Add a sponsor to the call"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mt: 1 }}
          />
        )}
        {addSponsor && (
          <Box sx={{ mt: 2 }}>
            {loadingSponsors ? (
              <CircularProgress size={24} />
            ) : sponsors.length > 0 ? (
              <List sx={{ py: 0, maxHeight: 200, overflowY: 'auto' }}>
                {sponsors.map((sponsor) => (
                  <ListItem
                    key={sponsor.id}
                    disablePadding
                  >
                    <ListItemButton
                      selected={selectedSponsor?.id === sponsor.id}
                      onClick={() => setSelectedSponsor(sponsor)}
                      sx={{ py: { xs: 1, sm: 1.5 } }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                          {sponsor.name?.charAt(0) || 'S'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={sponsor.name}
                        primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                No sponsors available
              </Typography>
            )}
          </Box>
        )}
        {(userType === 'physician' || user?.platform_role === 'super_admin') && (
          <FormControlLabel
            control={
              <Checkbox
                checked={addPhysician}
                onChange={(e) => setAddPhysician(e.target.checked)}
                name="addPhysician"
                color="primary"
                size="small"
              />
            }
            label="Add another physician for consultation"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mt: 1 }}
          />
        )}
        {addPhysician && (
          <Box sx={{ mt: 2 }}>
            {loadingPhysicians ? (
              <CircularProgress size={24} />
            ) : otherPhysicians.length > 0 ? (
              <List sx={{ py: 0, maxHeight: 200, overflowY: 'auto' }}>
                {otherPhysicians.map((physician) => (
                  <ListItem
                    key={physician.id}
                    disablePadding
                  >
                    <ListItemButton
                      selected={selectedPhysician?.id === physician.id}
                      onClick={() => setSelectedPhysician(physician)}
                      sx={{ py: { xs: 1, sm: 1.5 } }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                          {physician.name?.charAt(0) || 'P'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={physician.name}
                        secondary={physician.specialty || 'Physician'}
                        primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                No other physicians available
              </Typography>
            )}
          </Box>
        )}
        
        {/* Call Type Selection */}
        {selectedUser && (
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>Call Type</FormLabel>
              <RadioGroup
                row
                value={callType}
                onChange={(e) => setCallType(e.target.value)}
              >
                <FormControlLabel 
                  value="video" 
                  control={<Radio size="small" />} 
                  label="Video Call"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                />
                <FormControlLabel 
                  value="audio" 
                  control={<Radio size="small" />} 
                  label="Audio Only"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* Notes Field */}
        {selectedUser && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reason for call (optional)"
              placeholder="e.g., Follow-up consultation, emergency, etc."
              value={callNotes}
              onChange={(e) => setCallNotes(e.target.value)}
              size="small"
            />
          </Box>
        )}
        </Box> {/* End scrollable content */}
        
        {/* Fixed Footer with Buttons */}
        <Box sx={{ 
          flexShrink: 0, 
          mt: 2, 
          pt: 2, 
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex', 
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          justifyContent: 'flex-end',
          gap: { xs: 1, sm: 0 },
        }}>
          <Button 
            onClick={handleClose}
            fullWidth={false}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              order: { xs: 2, sm: 1 },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStartCall}
            disabled={!selectedUser || loading}
            fullWidth={false}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              ml: { xs: 0, sm: 1 },
              order: { xs: 1, sm: 2 },
              minHeight: { xs: 44, sm: 36 }, // Ensure touch-friendly size on mobile
            }}
          >
            {loading ? 'Starting...' : 'Start Call'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default StartVideoCallModal;
